// What counts as an occupied seat. One definition, so the dashboard's "3 / 5"
// and the server's "all seats are occupied" can never disagree.
//
// Enforcement lives on the server — set_firm_member_capabilities() in the
// database and the three Netlify gates (invite-firm-member, accept-firm-invite,
// update-firm-seats), all on the service role. This module is the client-side
// display of the same rule, and the place the rule is written down. Changing
// the definition means changing all five deliberately, not four by accident.

export interface SeatMember {
  has_seat: boolean;
}

/**
 * Occupied seats = members who hold one + invitations still outstanding.
 *
 * Two things this deliberately does NOT count:
 *
 * 1. **Members without a seat.** A firm admin who does not run projects is
 *    free (enterprise.md LOCKED #1: account admin is free, seats are paid).
 *    Counting membership — which every gate did until 2026-08-03 — billed
 *    firms for their unpaid admins and made the limit unenforceable.
 * 2. **Accepted or expired invitations.** An accepted one is already a seated
 *    member and would double-count; an expired one was never taken.
 *
 * Pending invitations DO count, because accepting one grants a seat. Leaving
 * them out would let an admin fill the firm with capability grants and turn
 * every outstanding invite into a "seats full" failure at accept time, after
 * the person had already been told they had a place.
 */
export function countOccupiedSeats(
  members: readonly SeatMember[],
  pendingInviteCount: number
): number {
  const seated = members.filter(m => m.has_seat).length;
  return seated + Math.max(0, pendingInviteCount);
}

/**
 * Seats available to hand out. Never negative: a firm whose seat count was
 * reduced below what is already held is over-subscribed, not owed seats, and
 * the UI should read "0 available" rather than a negative number.
 */
export function countAvailableSeats(
  seatCount: number,
  members: readonly SeatMember[],
  pendingInviteCount: number
): number {
  return Math.max(0, seatCount - countOccupiedSeats(members, pendingInviteCount));
}

/** Whether one more person can be given a seat right now. */
export function hasSeatCapacity(
  seatCount: number,
  members: readonly SeatMember[],
  pendingInviteCount: number
): boolean {
  return countOccupiedSeats(members, pendingInviteCount) < seatCount;
}
